import { errorClassName } from '../../../components/Error/Error';
import { formatPhoneNumber } from '../../../utils';
import { ParticipantProps } from '../../../utils/types';
import type { BankIDDataType } from './useBankIdConfirmation';

type StateValidation = {
    [key in PrefixesWithOwnRegexType]: string;
};

enum PrefixesWithOwnRegexType {
    CZECH = '+420',
    SLOVAK = '+421',
    POLAND = '+48',
}

export enum IdCardType {
    OP = 'OP',
    PA = 'PA',
    PP = 'PP',
    OTH = 'OTH',
}

export const regex = {
    CZECH_CELLPHONE_REGEX: '^(6|7)\\d{8}$',
    SLOVAK_CELLPHONE_REGEX: '^(9)\\d{8}$',
    POLAND_CELLPHONE_REGEX: '^[1-9]\\d{8}$',
    OTHER_CELLPHONE_REGEX: '^\\d{8,12}$',
    EMAIL_REGEX:
        '^(?:[a-zA-Z0-9!#$%&\'*+/=?^_`{|}~-]+(?:\\.[a-zA-Z0-9!#$%&\'*+/=?^_`{|}~-]+)*|"(?:[\\x01-\\x08\\x0b\\x0c\\x0e-\\x1f\\x21\\x23-\\x5b\\x5d-\\x7f]|\\\\[\\x01-\\x09\\x0b\\x0c\\x0e-\\x7f])*")@(?:(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?|\\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-zA-Z0-9-]*[a-zA-Z0-9]:(?:[\\x01-\\x08\\x0b\\x0c\\x0e-\\x1f\\x21-\\x5a\\x53-\\x7f]|\\\\[\\x01-\\x09\\x0b\\x0c\\x0e-\\x7f])+)\\])$',
};

const prefixesWithRegex: (PrefixesWithOwnRegexType | string)[] = Object.values(PrefixesWithOwnRegexType).map(
    (value) => value
);

export const participantInitialValues = (
    participant: ParticipantProps,
    isSupportedPrefix: boolean | undefined
): BankIDDataType => {
    return {
        titleBefore: participant?.PersonalIdentification?.TitleBefore ?? '',
        titleAfter: participant?.PersonalIdentification?.TitleAfter ?? '',
        birthPlace: participant?.PersonalIdentification?.Birthplace ?? '',
        birthCountry: participant?.PersonalIdentification?.BirthCountryCode ?? undefined,
        idCardCountry: participant?.IdCards[0].IdCardCountryCode ?? null,
        citizenship: participant?.PersonalIdentification?.CitizenshipCode ?? null,
        secondCitizenship: participant?.PersonalIdentification?.SecondCitizenshipCode ?? undefined,
        issuer: participant?.IdCards[0]?.Issuer ?? '',
        bankAccountPrefix: participant?.BankAccounts[0]?.Prefix ?? '',
        bankAccountNo: participant?.BankAccounts[0]?.BankAccountNo ?? '',
        bankAccountCode: participant?.BankAccounts[0]?.BankCode ?? null,
        email: validateEmail(participant?.Contact.Email),
        phone: validatePhone(
            participant?.Contact.Phone && isSupportedPrefix ? formatPhoneNumber(participant.Contact.Phone) : '',
            participant?.Contact.PhonePrefix.Prefix
        ),
        phonePrefix: isSupportedPrefix ? participant?.Contact.PhonePrefix.Prefix : '',
        validateSecondCitizenship: !!participant?.PersonalIdentification?.SecondCitizenshipCode,
        issueDate: (participant.IdCards || [])[0]?.IssuedDt ?? '',
    };
};

const validationPerState: StateValidation = {
    [PrefixesWithOwnRegexType.CZECH]: regex.CZECH_CELLPHONE_REGEX,
    [PrefixesWithOwnRegexType.SLOVAK]: regex.SLOVAK_CELLPHONE_REGEX,
    [PrefixesWithOwnRegexType.POLAND]: regex.POLAND_CELLPHONE_REGEX,
};

const validateEmail = (email: string | null) => (!email || !new RegExp(regex.EMAIL_REGEX).test(email) ? '' : email);

const validatePhone = (phone: string | null, prefix: string) => {
    if (!phone || !prefix) return '';

    const formattedPhone = phone.replaceAll(' ', '');

    const validation = prefixesWithRegex.includes(prefix)
        ? validationPerState[prefix as PrefixesWithOwnRegexType]
        : regex.OTHER_CELLPHONE_REGEX;

    if (!formattedPhone || !new RegExp(validation).test(formattedPhone)) return '';

    return formattedPhone;
};

export const scrollToError = () => {
    setTimeout(() => {
        const error = document.querySelector(`.${errorClassName}`);
        if (error) {
            error.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
    }, 250);
};

export const idTypeStaticMapper: { [K in IdCardType]: string } = {
    [IdCardType.OP]: 'Občanský průkaz',
    [IdCardType.PA]: 'Pas',
    [IdCardType.PP]: 'Povolení k pobytu',
    [IdCardType.OTH]: 'Jiné',
};
